/**
 * Size gate shared by the Saadhu hero and the product page.
 *
 * Wraps a size picker (snippets/saadhu-size-picker.liquid) and Horizon's `product-form-component`.
 * Plain HTMLElement on purpose: it only watches a native radio group and needs no refs or
 * declarative events of its own. The Add button stays focusable; until a size is chosen,
 * pressing it moves focus to the size picker and shows a message instead of adding anything.
 * Dispatches `saadhu:size` (bubbles) whenever the gate state or price changes.
 *
 * Fit option: CSS shows only the checked fit's sizes. Switching fit moves the chosen size to the
 * same size in the new fit, or clears it (and gates again) when that size is sold out.
 */
class SaadhuSizeGate extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.needsSize = this.dataset.needsSize === 'true';
    this.gated = this.needsSize && !this.querySelector('input[name="id"]:checked');
    this.#render();

    this.addEventListener('change', this.#onChange, { signal });
    // Capture phase: runs before Horizon's handlers on the inner product-form-component.
    this.addEventListener('submit', this.#onSubmit, { capture: true, signal });
    this.addEventListener('click', this.#onClick, { capture: true, signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  /** @returns {HTMLButtonElement | null} */
  get addButton() {
    return this.querySelector('button[name="add"]');
  }

  /** @returns {HTMLInputElement | null} */
  get variantInput() {
    return this.querySelector('input[ref="variantId"]');
  }

  /** Ask for a size: focus the picker and show the message. */
  requireSize() {
    if (!this.gated) return;
    const error = this.querySelector('[data-saadhu-error]');
    if (error instanceof HTMLElement) error.hidden = false;
    this.querySelector('[data-saadhu-sizes]')?.classList.add('saadhu-sizes--invalid');
    // First enabled size of the fit on show (the other fits' sizes are display: none).
    const target = [...this.querySelectorAll('input[name="id"]:not(:disabled)')].find(
      (radio) => radio.getClientRects().length > 0
    );
    if (target instanceof HTMLElement) {
      target.focus({ preventScroll: true });
      target.closest('[data-saadhu-sizes]')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  #render() {
    const button = this.addButton;
    if (button) button.setAttribute('aria-disabled', String(this.gated));
    this.classList.toggle('saadhu-size-gate--needs-size', this.gated);
    if (!this.gated) {
      const error = this.querySelector('[data-saadhu-error]');
      if (error instanceof HTMLElement) error.hidden = true;
      this.querySelector('[data-saadhu-sizes]')?.classList.remove('saadhu-sizes--invalid');
    }
    this.dispatchEvent(
      new CustomEvent('saadhu:size', {
        bubbles: true,
        detail: { gated: this.gated, price: this.querySelector('[data-saadhu-price]')?.textContent?.trim() ?? '' },
      })
    );
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.checked) return;
    if (input.dataset.saadhuFitChoice !== undefined) this.#onFit(input.dataset.saadhuFitChoice);
    else if (input.name === 'id') this.#select(input);
  };

  /** @param {string} fit */
  #onFit(fit) {
    const current = this.querySelector('input[name="id"]:checked');
    if (!(current instanceof HTMLInputElement)) return;
    const match = [...this.querySelectorAll(`[data-saadhu-fit="${fit}"] input[name="id"]`)].find(
      (radio) => radio instanceof HTMLInputElement && radio.dataset.size === current.dataset.size
    );
    if (match instanceof HTMLInputElement && !match.disabled) {
      match.checked = true;
      this.#select(match);
      return;
    }
    current.checked = false;
    if (this.variantInput) this.variantInput.value = '';
    this.gated = this.needsSize;
    this.#render();
  }

  /** @param {HTMLInputElement} input */
  #select(input) {
    if (this.variantInput) this.variantInput.value = input.value;
    const price = input.dataset.price;
    if (price) {
      // The product page also shows the price in the sticky bar and the MRP line, outside this element.
      const scope = this.closest('.shopify-section') ?? this;
      for (const node of scope.querySelectorAll('[data-saadhu-price]')) node.textContent = price;
    }
    const compare = input.dataset.compare ?? '';
    const scope = this.closest('.shopify-section') ?? this;
    for (const node of scope.querySelectorAll('[data-saadhu-compare]')) {
      node.textContent = compare;
      node.hidden = !compare;
    }
    this.gated = false;
    this.#render();
  }

  /** @param {Event} event */
  #block(event) {
    if (!this.gated) return;
    event.preventDefault();
    event.stopPropagation();
    this.requireSize();
  }

  /** @param {Event} event */
  #onSubmit = (event) => this.#block(event);

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest('button[name="add"]')) this.#block(event);
  };
}

if (!customElements.get('saadhu-size-gate')) {
  customElements.define('saadhu-size-gate', SaadhuSizeGate);
}
